/*
 * LegStyle: Make It Yours, <legstyle-make-it-yours>
 *
 * The design tool and the design-file download for sections/legstyle-make-it-yours.liquid.
 *
 * 6 Oct 2026 (Andre: "let people move and place an image, like placing where a tattoo goes on their leg"):
 * the picture is placed like a sticker on an otherwise plain cover, with four controls (around the leg, up and
 * down, zoom, turn) or by dragging on the flat preview. The pattern runs from top to bottom: the clear strips at
 * the knee and ankle (6 Oct, afternoon) were taken out that evening (Andre: "keep the pattern on the top and
 * bottom"); the template's own rim fade eases it out near the trims.
 * Before 6 Oct the visitor framed a square drawing that filled the whole cover.
 *
 * Pipeline:
 *   1. the picture into raised lines, once per picture or line setting, at the picture's own size (no more than
 *      STICKER_MAX px): even out the light, stretch, threshold (the prototype's maths);
 *   2. placed into the N x N drawing (the whole cover unwrapped: middle = front, edges = back), wrapping round
 *      the back;
 *   3. from here exactly the proven path: the 0.6 mm nozzle smoothing, the 3D preview, the 2048 px file.
 *
 * DO NOT CHANGE THE DOWNLOAD MATHS without a new render check through the Blender template:
 * N 1024, 2048 px output, sampleH, the tEXt "Comment" chunk after IHDR, the MY- reference, and RELIEF 1.3 mm in
 * the viewer (proven 5 Oct 2026: render_check_2026-10-05\README.md). Step 2 is new on 6 Oct and was re-checked
 * through the template the same day (same README). Since 10 Oct the file is saved as on screen (v3); intake's
 * make_reference.py turns it a quarter, and that turned file equals the proven v2 file pixel for pixel (checked
 * 10 Oct). DRAWING_SHIFT 0.25 now matters only for the 3D preview.
 *
 * The unwrapped cover is almost square on the preview cover (360 mm tall, about 370 mm round at the calf), so a
 * picture keeps its proportions there; it narrows a little towards the ankle, as on a real leg.
 *
 * Privacy: the picture is read in the browser and never sent anywhere by this file. Nothing here logs or
 * reports photo data, file names or settings.
 *
 * Section reloads (theme editor): connectedCallback sets up, disconnectedCallback removes listeners, timers,
 * the picture and any download link.
 */

const N = 1024;                 // working size of the height map (the whole cover, unwrapped)
// Pattern UVs (the template's, checked 4 Oct 2026): u 0 = inner side, 0.25 = back, 0.5 = outer side, 0.75 = front.
// The drawing's middle is the front of the leg, its edges meet at the back.
const DRAWING_SHIFT = 0.25;     // drawing x = cover u - 0.25
const MAX_PHOTO_SIDE = 3000;    // phone memory: larger photos are scaled down once
const STICKER_MAX = 1024;       // the picture's raised lines are worked out at no more than this size

/* ---------- picture to relief (prototype maths) ---------- */
function boxBlur(src, w, h, r) {
  const tmp = new Float32Array(w * h), out = new Float32Array(w * h), win = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    let acc = 0; const row = y * w;
    for (let x = -r; x <= r; x++) acc += src[row + Math.min(w - 1, Math.max(0, x))];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc / win;
      acc += src[row + Math.min(w - 1, x + r + 1)] - src[row + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / win;
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
    }
  }
  return out;
}
const blur3 = (a, w, h, r) => boxBlur(boxBlur(boxBlur(a, w, h, r), w, h, r), w, h, r);
const smooth = (e0, e1, x) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

const CRC = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
function crc32(bytes) { let c = 0xffffffff; for (let i = 0; i < bytes.length; i++) c = CRC[(c ^ bytes[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; }
async function fileKey(blob) {                  // size and CRC-32: enough to know the example file again
  const b = new Uint8Array(await blob.arrayBuffer());
  return { size: b.length, hash: crc32(b) };
}
async function withSettings(blob, text) {      // insert a tEXt chunk ("Comment") right after IHDR
  const png = new Uint8Array(await blob.arrayBuffer());
  const body = new TextEncoder().encode('Comment\0' + text.replace(/[^\x20-\x7e]/g, ' '));
  const chunk = new Uint8Array(12 + body.length), dv = new DataView(chunk.buffer);
  dv.setUint32(0, body.length); chunk.set([116, 69, 88, 116], 4); chunk.set(body, 8);
  dv.setUint32(8 + body.length, crc32(chunk.subarray(4, 8 + body.length)));
  const out = new Uint8Array(png.length + chunk.length);
  out.set(png.subarray(0, 33)); out.set(chunk, 33); out.set(png.subarray(33), 33 + chunk.length);
  return new Blob([out], { type: 'image/png' });
}

/* The placement, in plain numbers a visitor sees: around the leg in degrees (0 = front, +90 = inner side on a
   right leg, which is to the right as you face the cover; 180 = back; checked 6 Oct 2026), up and down in %
   (see geometry()), zoom (\`size\`) in % of the cover height (the picture's longer side), turn in degrees. */
const PLACE_DEFAULT = { around: 0, up: 45, size: 40, turn: 0 };
/* The date of the artwork-sharing wording on the page. Change it whenever that wording changes, and keep each
   version's text in 10_Website\Make_It_Yours\README.md, so a yes can be read against what the wearer saw. */
const ART_TERMS = '10 Oct 2026';

class LegStyleMakeItYours extends HTMLElement {
  connectedCallback() { this.init(); }
  disconnectedCallback() { this.teardown(); }

  init() {
    if (this._live) return;
    this._live = true;
    this._ac = new AbortController();
    const signal = this._ac.signal;
    const $ = (s) => this.querySelector(s);
    this.el = {
      file: $('[data-ls-file]'), fileLabel: $('[data-ls-file-label]'), adjust: $('[data-ls-adjust]'),
      drop: $('[data-ls-drop]'), dropmsg: $('[data-ls-dropmsg]'),
      place: $('[data-ls-place]'), around: $('[data-ls-around]'), up: $('[data-ls-up]'), size: $('[data-ls-size]'),
      turn: $('[data-ls-turn]'), reset: $('[data-ls-reset]'),
      raiseDark: $('[data-ls-raise="dark"]'), raiseLight: $('[data-ls-raise="light"]'), strength: $('[data-ls-strength]'),
      flatten: $('[data-ls-flatten]'), mapc: $('[data-ls-mapc]'), note: $('[data-ls-note]'),
      rights: $('[data-ls-rights]'), looked: $('[data-ls-looked]'), dl: $('[data-ls-download]'), dlmsg: $('[data-ls-dlmsg]'),
      fallback: $('[data-ls-fallback]'), settings: $('[data-ls-settings]'), copySet: $('[data-ls-copy-settings]'),
      copiedSet: $('[data-ls-copied-settings]'), colourName: $('[data-ls-colour-name]'), photos: $('[data-ls-photos]'),
      camera: $('[data-ls-camera]'), exampleTag: $('[data-ls-example]'),
      shareArt: $('[data-ls-share-art]'), credit: $('[data-ls-credit]'), creditRow: $('[data-ls-credit-row]'),
      designName: $('[data-ls-design-name]'),
    };
    this.viewer = $('legstyle-cover-viewer');
    this.radios = [...this.querySelectorAll('input.lsc-sw__input')];
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.state = { img: null, raiseDark: true, strength: 50, flatten: true, style: null, place: { ...PLACE_DEFAULT } };
    this.el.tidy = $('[data-ls-tidy]');     // "Tidy up a hand drawing": ticked = bold lines, unticked = shading kept
    this.sticker = null; this.heights = null;
    this.timer = null; this.placeTimer = null; this.exporting = false; this.lastUrl = null;
    this.drag = null;
    const on = (t, ev, fn, opts = {}) => t && t.addEventListener(ev, fn, { ...opts, signal });

    /* phones (Andre, 9 Oct 2026): the flat cover sits in the pinned panel, left of the 3D cover, so you drag on it and
       watch the cover while the steps scroll beneath. The same element moves there and back with the breakpoint. */
    // Astra, 9 Oct night: a class (not :has) sets the three columns; the flat cover always goes back to the top of
    // step 2's box, so a reconnect cannot lose its place; older browsers' media-query listener as a fallback.
    const panel = $('.lsc-viewer'), flat = this.el.drop, home = $('[data-ls-place] .lsc-placebox > div');
    if (panel && flat && home) {
      const mq = window.matchMedia('(max-width: 899px)');
      // Launch morning (Andre, 10 Oct 2026): on desktop too, the flat cover sits in the panel left of the 3D cover
      // (the steps stay on the right); `home` remains the place to return it if that is ever undone.
      // and on desktop the Turn slider sits under the flat cover; on phones it stays in step 2 (launch morning)
      const turnCtl = $('[data-ls-turnctl]'), sliders = $('.lsc-sliders');
      const place = () => {
        const phone = mq.matches;
        if (flat.parentNode !== panel) panel.prepend(flat);
        panel.classList.add('lsc-has-side');
        this.classList.add('lsc-flat-moved');
        if (turnCtl && sliders) {
          if (!phone && turnCtl.parentNode !== panel) flat.after(turnCtl);
          if (phone && turnCtl.parentNode !== sliders) sliders.append(turnCtl);
        }
        if (this.el.camera) this.el.camera.disabled = !phone;   // desktop: no camera button, so no field to Tab into
      };
      place();
      if (mq.addEventListener) mq.addEventListener('change', place, { signal });
      else if (mq.addListener) { mq.addListener(place); signal.addEventListener('abort', () => mq.removeListener(place)); }
    }

    /* colours */
    const checked = this.radios.find((r) => r.checked) || this.radios[0];
    for (const r of this.radios) on(r, 'change', () => { if (r.checked) { this.pickColour(r); this.touched(); } });
    if (checked) this.pickColour(checked);

    /* choosing a picture: the button, or dropping a file on the flat preview */
    on(this.el.file, 'change', (e) => { this.loadFile(e.target.files[0]); e.target.value = ''; });
    on(this.el.camera, 'change', (e) => { this.loadFile(e.target.files[0]); e.target.value = ''; });   // phones: "Take a photo"
    const drop = this.el.drop;
    on(drop, 'dragover', (e) => { e.preventDefault(); if (this.el.dropmsg) this.el.dropmsg.hidden = false; });
    on(drop, 'dragleave', () => { if (this.el.dropmsg) this.el.dropmsg.hidden = true; });
    on(drop, 'drop', (e) => { e.preventDefault(); if (this.el.dropmsg) this.el.dropmsg.hidden = true; this.loadFile(e.dataTransfer.files[0]); });

    /* placing it: four sliders, or drag on the flat preview (one finger moves it) */
    for (const k of ['around', 'up', 'size', 'turn']) {
      on(this.el[k], 'input', () => { this.state.place[k] = +this.el[k].value; this.syncSliders(); this.touched(); this.schedulePlace(); });
    }
    on(this.el.reset, 'click', () => { this.state.place = { ...PLACE_DEFAULT }; this.syncSliders(); this.touched(); this.schedulePlace(0); });
    const mc = this.el.mapc;
    /* Two fingers (Andre, 9 Oct 2026): pinch to make the picture bigger or smaller, twist to turn it, within the
       sliders' ranges. Lifting one finger carries on as a one-finger move from where that finger is. */
    const fingers = this.fingers = new Map();
    const pair = () => { const [a, b] = [...fingers.values()]; return { d: Math.hypot(b.x - a.x, b.y - a.y), a: Math.atan2(b.y - a.y, b.x - a.x) }; };
    const startPinch = () => { const g = pair(), p = this.state.place; this.pinch = { d0: Math.max(g.d, 1), a0: g.a, size0: p.size, turn0: p.turn }; this.drag = null; };
    on(mc, 'pointerdown', (e) => {
      if (!this.sticker) return;
      try { mc.setPointerCapture(e.pointerId); } catch { /* not capturable: the moves still arrive over the canvas */ }
      fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (fingers.size === 2) startPinch();
      else if (fingers.size === 1) this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY };
    });
    on(mc, 'pointermove', (e) => {
      if (fingers.has(e.pointerId)) fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pinch && fingers.size >= 2) {
        const g = pair(), pc = this.pinch, p = this.state.place;
        p.size = Math.min(300, Math.max(10, pc.size0 * g.d / pc.d0));
        let da = (g.a - pc.a0) * 180 / Math.PI;
        da = ((da + 540) % 360) - 180;                       // the shortest way round
        p.turn = ((pc.turn0 + da + 540) % 360) - 180;        // clockwise is positive, as the Turn slider
        this.syncSliders(); this.touched(); this.schedulePlace();
        return;
      }
      const d = this.drag; if (!d || d.id !== e.pointerId) return;
      const r = mc.getBoundingClientRect(), p = this.state.place;
      // a cover with no size (hidden, or mid-layout) would turn the move into "not a number" and lose the placement
      // (found 10 Oct 2026 in a hidden test pane): ignore the move instead
      if (!(r.width > 0 && r.height > 0)) return;
      const dx = (e.clientX - d.x) / r.width, dy = (e.clientY - d.y) / r.height;
      d.x = e.clientX; d.y = e.clientY;
      const gx = this.geometry();
      if (!gx.wide) p.around = ((p.around + dx * 360 + 540) % 360) - 180;   // the map's width is once round the leg
      else if (gx.panX > 0.005) p.around = Math.min(180, Math.max(-180, p.around + dx / gx.panX * 180));   // slides behind the join
      // the picture follows the finger: Up and down's range runs the other way when the picture is taller than the band
      const panY = this.geometry().panY;
      if (Math.abs(panY) > 0.01) p.up = Math.min(100, Math.max(0, p.up + dy / panY * 100));
      this.syncSliders(); this.touched(); this.schedulePlace();
    });
    // letting go: the full calculation and the 3D cover at once (during the drag only the quick preview runs)
    const endDrag = (e) => {
      fingers.delete(e.pointerId);
      if (this.pinch && fingers.size < 2) {                 // the pinch ends; a finger still down moves it from here
        this.pinch = null;
        const [id, f] = [...fingers.entries()][0] || [];
        this.drag = id === undefined ? null : { id, x: f.x, y: f.y };
        this.schedulePlace(0);
        return;
      }
      if (this.drag && this.drag.id === e.pointerId) { this.drag = null; this.schedulePlace(0); }
    };
    on(mc, 'pointerup', endDrag); on(mc, 'pointercancel', endDrag);
    // a finger lifted off the canvas where capture was refused or lost: let go of it all the same (Astra, 9 Oct night)
    on(window, 'pointerup', (e) => { if (fingers.has(e.pointerId)) endDrag(e); });
    on(window, 'pointercancel', (e) => { if (fingers.has(e.pointerId)) endDrag(e); });
    on(mc, 'lostpointercapture', (e) => { if (fingers.has(e.pointerId)) endDrag(e); });
    // keyboard on the flat preview: arrows move it (shift: further)
    on(mc, 'keydown', (e) => {
      if (!this.sticker) return;
      const step = e.shiftKey ? 10 : 2, p = this.state.place;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const d = (e.key === 'ArrowRight' ? 1 : -1) * step * 2;
        p.around = this.geometry().wide ? Math.min(180, Math.max(-180, p.around + d)) : ((p.around + d + 540) % 360) - 180;
      }
      else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // the picture moves the way the arrow points, whichever way Up and down's range runs
        const dir = (e.key === 'ArrowDown' ? 1 : -1) * (this.geometry().panY < 0 ? -1 : 1);
        p.up = Math.min(100, Math.max(0, p.up + dir * step));
      }
      // bigger or smaller from the keyboard, now that the Zoom slider is folded away (launch morning); Ctrl and
      // Cmd with + or - stay the browser's own zoom (Astra, 10 Oct)
      else if (e.ctrlKey || e.metaKey || e.altKey) return;
      else if (e.key === '+' || e.key === '=') p.size = Math.min(300, p.size * 1.05);
      else if (e.key === '-' || e.key === '_') p.size = Math.max(10, p.size / 1.05);
      else return;
      e.preventDefault(); this.syncSliders(); this.touched(); this.schedulePlace();
    });
    /* the scroll wheel over the flat cover makes the picture bigger or smaller (Andre, 10 Oct 2026); a laptop
       trackpad's pinch arrives as a wheel event with ctrlKey, and is taken a little faster. At the smallest or
       largest size the wheel scrolls the page again, and a turn that changes nothing changes nothing (Astra, 10 Oct). */
    on(mc, 'wheel', (e) => {
      if (!this.sticker || !e.deltaY) return;
      const p = this.state.place;
      const size = Math.min(300, Math.max(10, p.size * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015))));
      if (Math.abs(size - p.size) < 1e-6) { if (e.ctrlKey) e.preventDefault(); return; }   // no page zoom from a pinch
      e.preventDefault();
      p.size = size;
      this.syncSliders(); this.touched(); this.schedulePlace();
    }, { passive: false });

    /* lines or shading */
    const pickStyle = (s) => {
      const st = this.state; if (!st.img || st.style === s) return;
      st.style = s; st.flatten = s === 'lines'; if (this.el.flatten) this.el.flatten.checked = st.flatten;
      st.raiseDark = s === 'shading' ? false : null;          // lines: decided again from the picture
      this.showStyle(); this.touched(); this.scheduleProcess(0);
    };
    on(this.el.tidy, 'change', (e) => pickStyle(e.target.checked ? 'lines' : 'shading'));

    /* line controls */
    on(this.el.raiseDark, 'click', () => { this.state.raiseDark = true; this.showRaise(); this.touched(); this.scheduleProcess(0); });
    on(this.el.raiseLight, 'click', () => { this.state.raiseDark = false; this.showRaise(); this.touched(); this.scheduleProcess(0); });
    on(this.el.strength, 'input', (e) => { this.state.strength = +e.target.value; this.touched(); this.scheduleProcess(120); });
    on(this.el.flatten, 'change', (e) => { this.state.flatten = e.target.checked; this.touched(); this.scheduleProcess(0); });

    /* sending: the download waits for the two ticks. The photos answer is optional and goes inside the file
       (Andre, 7 Oct 2026: the wearer's own yes travels with the design); a change after a download asks for a
       fresh download, so the file the clinician sends carries the answer the wearer gave last. */
    // the same for the artwork-sharing answer and its credit name (Andre, 10 Oct 2026)
    const answerChanged = (what) => {
      this.showSettings();
      this.rev = (this.rev || 0) + 1;          // a file being made now would carry the old answer
      if (this.sent) this.hideSend();          // the file on show carries the old answer: download again (Astra, 10 Oct)
      if (this.lastUrl && this.el.dlmsg) this.el.dlmsg.textContent = `You changed your answer about ${what}. Download your design file again so it carries your answer.`;
    };
    on(this.el.photos, 'change', () => answerChanged('photos'));
    // sharing is answered in the send panel, after the download: it goes in the email, not the file
    on(this.el.shareArt, 'change', () => { if (this.el.creditRow) this.el.creditRow.hidden = !this.el.shareArt.checked; });
    // the design needs a name before the download (Andre, 18 Oct 2026): it is how the clinician's order finds it
    this.updateTicks = () => { if (this.el.dl) this.el.dl.disabled = !(this.el.rights && this.el.rights.checked && this.el.looked && this.el.looked.checked) || (this.el.designName && !this.designName()) || this.exporting || !!this.pendingImg; };   // the example may be downloaded too (Andre, 10 Oct)
    on(this.el.rights, 'change', this.updateTicks); on(this.el.looked, 'change', this.updateTicks); this.updateTicks();
    // a new name after a download: the file on show carries the old one, so download again (as answerChanged does)
    on(this.el.designName, 'input', () => {
      this.updateTicks();
      if (this.sent) {
        this.hideSend();
        if (this.el.dlmsg) this.el.dlmsg.textContent = 'You changed the name. Download your design file again so it carries the new name.';
      }
    });
    on(this.el.dl, 'click', () => this.download());
    on(this.el.copySet, 'click', () => this.copyText(this.settingsCode(), this.el.settings, this.el.copiedSet));
    /* sending to us (Andre, 10 Oct 2026) */
    const sendBox = $('[data-ls-send]');
    if (sendBox) {
      on(sendBox.querySelector('[data-ls-share-file]'), 'click', () => this.shareFile());
      // the email link is written as it is used, so it carries the latest sharing answer and credit
      on(sendBox.querySelector('[data-ls-mailto]'), 'click', (e) => { if (!this.sent) { e.preventDefault(); return; } e.currentTarget.href = this.mailtoHref(); });
    }
    this.syncSliders();
    this.setPlaceEnabled(false);
    this.drawMap();
    this.showSettings();

    /* the example picture (Andre, 9 Oct 2026): loaded only if nothing has been chosen meanwhile */
    const ex = this.dataset.example, seq0 = this.pickSeq || 0;
    if (ex) {
      fetch(ex).then((r) => (r.ok ? r.blob() : null)).then(async (b) => {
        if (!b) return;
        this.exampleKey = await fileKey(b);          // the same file chosen later (saved from the site) stays an example
        if (!this._live || this.state.img || (this.pickSeq || 0) !== seq0) return;
        this.loadFile(new File([b], 'example.png', { type: b.type || 'image/png' }), { example: true });
      }).catch(() => {});
    }
  }

  teardown() {
    if (!this._live) return;
    this._live = false;
    this._ac.abort();
    clearTimeout(this.timer); clearTimeout(this.placeTimer);
    this.pickSeq = (this.pickSeq || 0) + 1;                 // a picture still loading must not land after teardown
    this.cancelPending();
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = 0; }
    if (this.lastUrl) { URL.revokeObjectURL(this.lastUrl); this.lastUrl = null; }
    this.releasePicture();
    if (this.sticker && this.sticker.preview) { this.sticker.preview.width = this.sticker.preview.height = 0; }
    this.sticker = null; this.heights = null;
  }

  /* ---------- colours ---------- */
  pickColour(r) {
    this.colourCode = r.value;
    if (this.el.colourName) this.el.colourName.replaceChildren(Object.assign(document.createElement('b'), { textContent: r.value }), ' ' + r.dataset.name);
    const line = this.querySelector('[data-ls-pickline-colour]');                 // phones: under the pinned cover
    if (line) line.textContent = `${r.dataset.name} (${r.value})`;
    if (this.viewer && this.viewer.setColour) this.viewer.setColour(r.dataset.hex);
    this.showSettings();
  }

  /* ---------- the picture ---------- */
  releasePicture() {             // phone memory: let go of the previous photo at once
    const old = this.state && this.state.img;
    if (old && old instanceof HTMLCanvasElement) { old.width = 0; old.height = 0; }
    if (this.state) this.state.img = null;
  }
  /* A large photo is drawn once into a canvas no bigger than MAX_PHOTO_SIDE, and the full-size image is let go. */
  boundPicture(img) {
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height, k = MAX_PHOTO_SIDE / Math.max(w, h);
    if (k >= 1) return img;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
    const x = c.getContext('2d'); x.imageSmoothingQuality = 'high'; x.drawImage(img, 0, 0, c.width, c.height);
    img.src = '';
    return c;
  }
  /* A picture still loading is dropped: its handlers cleared and its file let go (a cancelled load never fires onload,
     so its own revoke would never run). */
  cancelPending() {
    if (this.pendingImg) { this.pendingImg.onload = this.pendingImg.onerror = null; this.pendingImg.src = ''; this.pendingImg = null; }
    if (this.pendingUrl) { URL.revokeObjectURL(this.pendingUrl); this.pendingUrl = null; }
  }
  async loadFile(file, { example = false } = {}) {
    if (!file || !file.type.startsWith('image/')) { this.note('That file is not a picture. Try a photo (JPG or PNG).', true); return; }
    // the example saved from the site and chosen as one's own is still the example (legstyle-reviewer, 9 Oct night),
    // so its design file says "LegStyle Ginkgo Leaf example", not "my own" (downloadable since 10 Oct)
    if (!example && this.exampleKey && file.size === this.exampleKey.size && (await fileKey(file)).hash === this.exampleKey.hash) {
      example = true;
    }
    // only the latest choice may land (Astra, 6 Oct 2026: choosing A then B could end on A if A decoded last);
    // the download waits while a new picture loads
    const my = this.pickSeq = (this.pickSeq || 0) + 1;
    this.cancelPending();
    if (this.el.dl) this.el.dl.disabled = true;
    const url = URL.createObjectURL(file); const img = new Image();
    this.pendingImg = img; this.pendingUrl = url;
    img.onerror = () => { URL.revokeObjectURL(url); if (my !== this.pickSeq) return; this.pendingImg = null; this.pendingUrl = null; if (this.updateTicks) this.updateTicks(); this.note('We could not open that picture. Try a JPG or PNG.', true); };
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (!this._live || my !== this.pickSeq) return;
      this.pendingImg = null; this.pendingUrl = null;
      if (this.updateTicks) this.updateTicks();
      let pic;
      try { pic = this.boundPicture(img); } catch { this.note('We could not open that picture. Try a JPG or PNG.', true); return; }
      this.releasePicture();
      const st = this.state;
      st.img = pic; st.raiseDark = null; st.flatten = true; st.style = null; st.place = { ...PLACE_DEFAULT };
      // the example: marked; it may be downloaded as a design (Andre, 10 Oct 2026: "you can do a lot with it by
      // zooming in"); the first real picture takes its place
      const wasExample = st.example;
      st.example = example;
      if (this.el.exampleTag) this.el.exampleTag.hidden = !example;
      const artBox = this.querySelector('[data-ls-art-fieldset]'); if (artBox) artBox.hidden = example;   // not for our leaf
      if (this.updateTicks) this.updateTicks();
      if (this.el.dlmsg) {
        if (example) this.el.dlmsg.textContent = 'The leaf is our example: make it yours by moving and zooming it, or choose your own picture.';
        else if (wasExample) this.el.dlmsg.textContent = '';
      }
      if (this.el.flatten) this.el.flatten.checked = true;
      if (this.el.fileLabel) this.el.fileLabel.textContent = example ? 'Choose your own picture' : 'Change picture';
      this.touched();
      this.syncSliders(); this.setPlaceEnabled(true);
      this.scheduleProcess(0);
      if (!example && this.el.place) this.el.place.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
    };
    img.src = url;
  }
  /* any change to the design: the "I have checked it" tick asks again (Astra, 6 Oct 2026) */
  touched() {
    this.rev = (this.rev || 0) + 1;     // a file being made now is out of date (download() checks)
    if (this.sent) this.hideSend();     // the code and file must match: a changed design is downloaded again first
    if (this.el.looked && this.el.looked.checked) {
      this.el.looked.checked = false; if (this.updateTicks) this.updateTicks();
      if (this.el.dlmsg) this.el.dlmsg.textContent = 'You changed your design. When you are happy with it, tick "I have checked" again.';
    }
  }
  syncSliders() {
    const p = this.state.place;
    for (const k of ['around', 'up', 'size', 'turn']) if (this.el[k]) this.el[k].value = Math.round(p[k]);
    // what a screen reader says for each slider (the numbers alone mean little)
    const a = Math.round(p.around), t = Math.round(p.turn), text = {
      around: a === 0 ? 'on the front' : Math.abs(a) >= 178 ? 'at the back' : `${Math.abs(a)} degrees to the ${a > 0 ? 'right' : 'left'} of the front`,
      up: `${Math.round(p.up)} percent of the way down`,
      size: `${Math.round(p.size)} percent of the cover's height${p.size > 100 ? ', so the cover shows part of it' : ''}`,
      turn: t === 0 ? 'upright' : `turned ${Math.abs(t)} degrees ${t > 0 ? 'clockwise' : 'anticlockwise'}`,
    };
    for (const k in text) if (this.el[k]) this.el[k].setAttribute('aria-valuetext', text[k]);
  }
  setPlaceEnabled(on) {
    for (const k of ['around', 'up', 'size', 'turn', 'reset', 'tidy']) if (this.el[k]) this.el[k].disabled = !on;
  }

  /* ---------- 1. the picture into raised lines (prototype maths, at the picture's own size) ---------- */
  scheduleProcess(ms) { clearTimeout(this.timer); this.timer = setTimeout(() => this.processPicture(), ms); }
  processPicture() {
    this.timer = null;
    const st = this.state;
    if (!st.img || !this._live) return;
    const w0 = st.img.naturalWidth || st.img.width, h0 = st.img.naturalHeight || st.img.height;
    const k = Math.min(1, STICKER_MAX / Math.max(w0, h0));
    const w = Math.max(8, Math.round(w0 * k)), h = Math.max(8, Math.round(h0 * k));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(st.img, 0, 0, w, h);
    const d = ctx.getImageData(0, 0, w, h).data, g = new Float32Array(w * h), alpha = new Float32Array(w * h);
    c.width = c.height = 0;
    // see-through parts read as white paper, and are never raised, whichever side is raised (Astra, 6 Oct)
    for (let i = 0, j = 0; i < g.length; i++, j += 4) {
      const al = d[j + 3] / 255; alpha[i] = al;
      g[i] = al * (0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2]) / 255 + (1 - al);
    }
    /* Lines or shading (Andre, 6 Oct 2026: "it strips a lot of detail away, perhaps we could keep it if it's not
       hand drawn"). A picture with many in-between greys (a photo, an engraving) starts as "Keep the shading":
       its greys become heights, the way the template's Displace reads any artwork (measured that evening: height =
       (grey - 0.5) x 1.3 mm, a straight line). A clean drawing starts as "Bold lines". The visitor can switch. */
    /* Andre, 10 Oct 2026 night: the automatic "Tidy up a hand drawing" "has generally made things worse" and could
       confuse, with no obvious undo. Own pictures now always start with their shading kept, exactly as unticking it
       did (light parts up, no evening out), and the tick is hidden ("keep it but not show it"). The Ginkgo example
       keeps the guess below, so it looks as it did. To bring the choice back: unhide [data-ls-tidy]'s label. */
    if (st.style === null && !st.example) {
      st.style = 'shading'; st.flatten = false; st.raiseDark = false;
      if (this.el.flatten) this.el.flatten.checked = false;
    }
    if (st.style === null) {
      let midN = 0, allN = 0;
      for (let i = 0; i < g.length; i += 3) { if (alpha[i] > 0.5) { allN++; if (g[i] > 0.15 && g[i] < 0.85) midN++; } }
      st.style = allN && midN / allN > 0.3 ? 'shading' : 'lines';
      st.flatten = st.style === 'lines';                       // evening out the light suits paper, not photos
      if (this.el.flatten) this.el.flatten.checked = st.flatten;
      if (st.style === 'shading') st.raiseDark = false;        // as in Blender: the light parts stand up
    }
    this.showStyle();
    let v = g;
    if (st.flatten) {                       // even out uneven light on paper (radius as the 1024 px square had)
      const bg = blur3(g, w, h, Math.max(4, Math.round(24 * Math.max(w, h) / 1024))); v = new Float32Array(w * h);
      for (let i = 0; i < v.length; i++) v[i] = Math.min(1, g[i] / Math.max(bg[i], 0.06));
    }
    // stretch to the full range (2nd to 98th percentile); a sparse drawing must not collapse the stretch
    const hist = new Uint32Array(256); for (let i = 0; i < v.length; i += 7) hist[Math.min(255, (v[i] * 255) | 0)]++;
    const tot = hist.reduce((a, b) => a + b, 0); let acc = 0, lo = 0, hi = 255;
    for (let q = 0; q < 256; q++) { acc += hist[q]; if (acc < tot * 0.003) lo = q; if (acc < tot * 0.997) hi = q + 1; }
    let span = (hi - lo) / 255, base = lo / 255;
    if (span < 0.2) { base = 0; span = 1; }
    let mean = 0; for (let i = 0; i < v.length; i += 7) mean += v[i]; mean /= v.length / 7;
    if (st.raiseDark === null) { st.raiseDark = mean > 0.5; }
    this.showRaise();
    const t = 0.78 - (st.strength / 100) * 0.56;
    const contrast = Math.pow(2, (st.strength - 50) / 50);     // shading: "Picture detail" is contrast, x0.5 to x2
    const bin = new Float32Array(w * h);
    for (let i = 0; i < bin.length; i++) {
      let x = Math.min(1, Math.max(0, (v[i] - base) / span));
      if (st.raiseDark) x = 1 - x;
      const keepIn = smooth(0.25, 0.75, alpha[i]);
      if (st.style === 'shading') {
        // the picture's own background counts in shading, so its edges fade out over 4% of its size rather than
        // stopping like a tile (render, 6 Oct 2026: a hard edge showed down the outer side)
        const px = (i % w + 0.5) / w, py = (Math.floor(i / w) + 0.5) / h;
        const edge = smooth(0, 0.04, Math.min(px, 1 - px, py, 1 - py));
        bin[i] = Math.min(1, Math.max(0, (x - 0.5) * contrast + 0.5)) * keepIn * edge;
      } else bin[i] = smooth(t - 0.05, t + 0.05, x) * keepIn;
    }
    // the quick-preview image: white where raised, see-through elsewhere (drawn while dragging)
    let ink = 0;
    const pc = document.createElement('canvas'); pc.width = w; pc.height = h;
    const pctx = pc.getContext('2d'), pim = pctx.createImageData(w, h);
    for (let i = 0, j = 0; i < bin.length; i++, j += 4) {
      if (bin[i] > 0.5) ink++;
      pim.data[j] = pim.data[j + 1] = pim.data[j + 2] = 255; pim.data[j + 3] = Math.round(bin[i] * 255);
    }
    pctx.putImageData(pim, 0, 0);
    if (this.sticker && this.sticker.preview) { this.sticker.preview.width = this.sticker.preview.height = 0; }
    this.sticker = { bin, w, h, ink, preview: pc, style: st.style };
    this.placeSticker();
  }

  /* ---------- 2. placed on the unwrapped cover, the strips kept clear ---------- */
  /* While a slider moves or a finger drags, only a quick preview is drawn (once a frame); the full calculation
     and the 3D cover follow when the movement pauses (reviewer, 6 Oct 2026: a phone cannot redo the whole
     cover 25 times a second). */
  schedulePlace(ms = 180) {
    if (!this.sticker) return;
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.previewPlace(); });
    clearTimeout(this.placeTimer); this.placeTimer = setTimeout(() => this.placeSticker(), ms);
  }
  /* The picture's centre, size and turn in drawing units (0..1 across = once round, 0..1 down = the height).
     The cover is a window onto the picture (Andre, 6 Oct 2026: "zoom crops the image and then that cropped image is
     used on around the leg and up/down"). Zoom may make the picture bigger than the cover: what lies beyond once
     round, or beyond the top and bottom, is simply not on the cover. Around the leg moves it round (once it goes all
     the way round, it slides behind a join fixed at the back). Up and down keeps a smaller picture on the cover,
     and slides a taller one to choose the part shown. */
  geometry() {
    const sk = this.sticker, p = this.state.place;
    const L = Math.max(0.03, p.size / 100);                // the picture's longer side, in cover heights
    const sw = sk.w >= sk.h ? L : L * sk.w / sk.h, sh = sk.h >= sk.w ? L : L * sk.h / sk.w;
    const turn = p.turn * Math.PI / 180, cs = Math.abs(Math.cos(turn)), sn = Math.abs(Math.sin(turn));
    const across = sw * cs + sh * sn, tall = sw * sn + sh * cs; // the turned picture's width and height
    // A picture narrower than once round moves round the leg like a sticker (it has no join). One that goes all the
    // way round keeps its join at the back (Andre, 6 Oct 2026: "keep the seam on the cover at the rear"): Around the
    // leg then slides the picture behind that fixed join, over the width it has to spare.
    const wide = across >= 1, panX = wide ? (across - 1) / 2 : 0;
    const cx = 0.5 + (wide ? (p.around / 180) * panX : p.around / 360);   // drawing x of the centre (0.5 = front)
    const lo = tall / 2, hi = 1 - tall / 2;                // centre range: on the cover, or panning
    const cy = lo + (hi - lo) * (p.up / 100);              // drawing y of the picture's centre
    return { cx, cy, sw, sh, turn, wide, round: wide, fills: tall >= 1, panX, panY: hi - lo };
  }
  previewPlace() {
    const sk = this.sticker, mc = this.el.mapc;
    if (!sk || !mc || !this._live) return;
    const g = this.geometry(), S = mc.width, x = mc.getContext('2d');
    this.drawMapBase(null);
    x.save();
    // all the way round: one picture, its join at the back (the canvas edges); otherwise a copy either side, so a
    // smaller picture crossing the back shows on both edges
    for (const off of g.wide ? [0] : [-S, 0, S]) {
      x.save();
      // each copy covers once round, centred on the picture's middle (as the full calculation's nearest copy)
      x.beginPath(); x.rect(g.cx * S + off - S / 2, 0, S, S); x.clip();
      x.translate(g.cx * S + off, g.cy * S); x.rotate(g.turn);
      x.drawImage(sk.preview, -g.sw * S / 2, -g.sh * S / 2, g.sw * S, g.sh * S);
      x.restore();
    }
    x.restore();
    this.drawMapMarks();
  }
  placeSticker() {
    this.placeTimer = null;
    const sk = this.sticker;
    if (!sk || !this._live) return;
    const { cx, cy, sw, sh, turn, round, fills, wide } = this.geometry();
    this.round = round; this.fills = fills;
    const a = -turn, ca = Math.cos(a), sa = Math.sin(a);
    const bin = new Float32Array(N * N);
    let inside = 0;
    for (let yy = 0; yy < N; yy++) {
      const y = (yy + 0.5) / N, dy = y - cy;
      const row = yy * N;
      for (let xx = 0; xx < N; xx++) {
        let dx = (xx + 0.5) / N - cx;
        if (!wide) dx -= Math.round(dx);                   // a smaller picture wraps round the back: the nearest copy
        const ux = (dx * ca - dy * sa) / sw + 0.5, uy = (dx * sa + dy * ca) / sh + 0.5;
        if (ux <= 0 || ux >= 1 || uy <= 0 || uy >= 1) continue;
        // bilinear in the picture's raised lines
        const fx = ux * sk.w - 0.5, fy = uy * sk.h - 0.5;
        const x0 = Math.max(0, Math.min(sk.w - 2, Math.floor(fx))), y0 = Math.max(0, Math.min(sk.h - 2, Math.floor(fy)));
        const tx = Math.min(1, Math.max(0, fx - x0)), ty = Math.min(1, Math.max(0, fy - y0)), i = y0 * sk.w + x0;
        const val = (sk.bin[i] * (1 - tx) + sk.bin[i + 1] * tx) * (1 - ty) + (sk.bin[i + sk.w] * (1 - tx) + sk.bin[i + sk.w + 1] * tx) * ty;
        if (val > 0.5) inside++;
        bin[row + xx] = val;
      }
    }
    this.finishRelief(bin, inside);
  }

  /* ---------- 3. the proven path: nozzle smoothing, notes, the 3D preview ---------- */
  finishRelief(bin, inside) {
    const shading = this.sticker && this.sticker.style === 'shading';
    let heights, raised = 0, lost = 0;
    if (shading) {
      // shading kept: the greys are the heights, as Blender reads them; only a light blur (about 0.3 mm)
      heights = boxBlur(bin, N, N, 1);
      for (let i = 0; i < heights.length; i++) if (heights[i] > 0.5) raised++;
    } else {
      // lines: what a 0.6 mm nozzle can hold: features under about 1.2 mm across fade away
      const soft = boxBlur(boxBlur(bin, N, N, 3), N, N, 3);
      const hgt = new Float32Array(N * N);
      for (let i = 0; i < hgt.length; i++) {
        hgt[i] = smooth(0.22, 0.7, soft[i]);
        if (bin[i] > 0.5) { raised++; if (hgt[i] < 0.3) lost++; }
      }
      heights = boxBlur(hgt, N, N, 1);
    }
    this.heights = heights;
    this.drawMap(bin);
    const frac = raised / (N * N), lostFrac = raised ? lost / raised : 0;
    let adjust = false;
    if (frac < 0.002 && this.sticker && this.sticker.ink > 0 && inside > 0)
      this.note('Your picture is too small or too fine to be raised. Make it bigger.', true);
    else if (frac < 0.002) { this.note('Nothing would be raised. Adjust the picture below.', true); adjust = true; }
    else if (frac > 0.6 && !shading) { this.note('Most of the cover would be raised. Adjust the picture below.', true); adjust = true; }
    else if (lostFrac > 0.18) this.note('Some details may be too small. They are marked in orange. Try making the picture bigger.', true);
    // the join stays at the back (Andre, 9 Oct 2026: a seam on the front would disappoint in real life), so a picture
    // just past once round can only slide a little: say so, and how to get more
    else if (this.round) this.note('Your picture now goes all the way round. Its join stays at the back. Make it bigger to slide it further.', false);
    else if (this.fills) this.note('Your picture fills the cover from top to bottom. Up and down chooses the part that shows.', false);
    else this.note('Turn the cover to check it from all sides.', false);
    if (adjust && this.el.adjust) this.el.adjust.open = true;     // open the adjustments when they are needed
    this.showSettings();
    if (this.viewer && this.viewer.setRelief) this.viewer.setRelief(this.heights, N, DRAWING_SHIFT);
  }
  /* the flat preview: the whole cover unwrapped, front and back marked */
  drawMap(bin) { this.drawMapBase(bin); this.drawMapMarks(); }
  drawMapBase(bin) {
    const mc = this.el.mapc; if (!mc) return;
    const S = mc.width, x = mc.getContext('2d'), im = x.createImageData(S, S);
    const css = getComputedStyle(this);
    const warn = css.getPropertyValue('--lsc-warn').trim() || '#b5461a';
    const tmp = document.createElement('canvas').getContext('2d'); tmp.fillStyle = warn; tmp.fillRect(0, 0, 1, 1);
    const wc = tmp.getImageData(0, 0, 1, 1).data, hgt = this.heights;
    for (let yy = 0; yy < S; yy++) {
      for (let xx = 0; xx < S; xx++) {
        const o = (yy * S + xx) * 4;
        let g = 26;
        if (hgt && bin) {
          const i = Math.floor(yy * N / S) * N + Math.floor(xx * N / S);
          if (bin[i] > 0.5 && hgt[i] < 0.3) { im.data[o] = wc[0]; im.data[o + 1] = wc[1]; im.data[o + 2] = wc[2]; im.data[o + 3] = 255; continue; }
          g = Math.round(hgt[i] * 229 + 26);
        }
        im.data[o] = im.data[o + 1] = im.data[o + 2] = g; im.data[o + 3] = 255;
      }
    }
    x.putImageData(im, 0, 0);
  }
  drawMapMarks() {
    const mc = this.el.mapc; if (!mc) return;
    const S = mc.width, x = mc.getContext('2d');
    // marks: front down the middle, back at both edges
    x.save();
    x.strokeStyle = 'rgba(255,255,255,0.35)'; x.setLineDash([4, 5]); x.lineWidth = 1;
    x.beginPath(); x.moveTo(S / 2 + 0.5, 0); x.lineTo(S / 2 + 0.5, S); x.stroke();
    x.setLineDash([]); x.fillStyle = 'rgba(255,255,255,0.8)';
    x.font = `${Math.round(S / 26)}px system-ui, sans-serif`; x.textBaseline = 'middle';
    x.textAlign = 'center'; x.fillText('front', S / 2, S * 0.035);
    x.textAlign = 'left'; x.fillText('back', 6, S * 0.5);
    x.textAlign = 'right'; x.fillText('back', S - 6, S * 0.5);
    x.restore();
  }
  note(msg, warn) { if (!this.el.note) return; this.el.note.textContent = msg; this.el.note.classList.toggle('is-warn', !!warn); }
  showStyle() {
    if (this.el.tidy) this.el.tidy.checked = this.state.style === 'lines';
  }
  showRaise() {
    if (!this.el.raiseDark) return;
    this.el.raiseDark.setAttribute('aria-pressed', String(!!this.state.raiseDark));
    this.el.raiseLight.setAttribute('aria-pressed', String(!this.state.raiseDark));
  }
  sampleH(u, v) {                // bilinear; u across, v from the top
    const heights = this.heights;
    const x = Math.min(N - 1.001, Math.max(0, u * (N - 1))), y = Math.min(N - 1.001, Math.max(0, v * (N - 1)));
    const x0 = x | 0, y0 = y | 0, fx = x - x0, fy = y - y0, i = y0 * N + x0;
    return (heights[i] * (1 - fx) + heights[i + 1] * fx) * (1 - fy) + (heights[i + N] * (1 - fx) + heights[i + N + 1] * fx) * fy;
  }

  /* ---------- the settings code: everything needed to recreate the preview from the original picture ---------- */
  settingsCode() {
    const st = this.state, p = st.place;
    const parts = [
      // v3: the file is laid out as the wearer saw it; make_reference.py reads this line and turns it for the template
      'LegStyle Make It Your Own v3',
      'layout as on screen (join at the back)',   // wearers may see this (Copy settings): plain words only
      // the Ginkgo Leaf example is LegStyle's own released artwork, so the file says so (Andre, 10 Oct 2026)
      st.example ? 'picture: LegStyle Ginkgo Leaf example (our artwork)' : 'picture: my own (attached)',
      `colour ${this.colourCode}`,
      `around ${Math.round(p.around)} degrees (0 = front)`,
      `up ${Math.round(p.up)}%`,
      `zoom ${Math.round(p.size)}% (longer side, of the cover height)`,
      `turn ${Math.round(p.turn)} degrees`,
      `style ${st.style === 'shading' ? 'shading kept' : 'bold lines'}`,
      `raise ${st.raiseDark ? 'dark' : 'light'}`,
      `strength ${st.strength}`,
      `even out shadows ${st.flatten ? 'on' : 'off'}`,
      `photos of the finished cover ${this.el && this.el.photos && this.el.photos.checked ? 'yes' : 'no'}`,
    ];
    return parts.join(' · ');
  }
  showSettings() { if (this.el && this.el.settings && this.state) this.el.settings.textContent = this.settingsCode(); }
  // never for the Ginkgo example: it is LegStyle's artwork, not the wearer's (legstyle-reviewer, 10 Oct)
  shareArt() { return !!(this.el && this.el.shareArt && this.el.shareArt.checked && this.state && !this.state.example); }
  creditName() { return (this.el && this.el.credit ? this.el.credit.value : '').replace(/\s+/g, ' ').trim().slice(0, 60); }
  designName() { return (this.el && this.el.designName ? this.el.designName.value : '').replace(/\s+/g, ' ').trim().slice(0, 40); }
  /* ---------- sending (Andre, 10 Oct 2026) ----------
     After a download: the two ways to send. The email to us is written for them (the design code, which they need
     not pass on, their design's name and a place for their clinician, and their answers; the first name until 18 Oct); a page cannot attach a file to an
     email link, so where the device can share files the button attaches it through the share menu, and otherwise
     (or once that menu is closed) they attach it.
     A yes to sharing their design is a tick in the send panel (shown after the download) and travels in that email (not the file). */
  showSend(ref, name, blob, designName) {
    const box = this.querySelector('[data-ls-send]'); if (!box) return;
    this.sent = { ref, name, blob, designName };
    const artBox = box.querySelector('[data-ls-art-fieldset]'); if (artBox) artBox.hidden = !!this.state.example;   // not for our leaf
    const file = new File([blob], name, { type: 'image/png' });
    // ONE button, "Email it to LegStyle" (Andre, 10 Oct: three were confusing; he wants the file attached): wherever
    // the device can share files, phones and computers, it opens the share menu so the email app gets the file
    // attached (Outlook on Andre's PC did). A computer's share menu may have no email app (webmail is not in it), so
    // closing it, or any failure, turns the button into the plain email link (shareFile).
    this.useShare(!!(navigator.canShare && navigator.canShare({ files: [file] })));
    box.hidden = false;
  }
  useShare(yes) {
    const box = this.querySelector('[data-ls-send]'); if (!box || !this.sent) return;
    const share = box.querySelector('[data-ls-share-file]'), mail = box.querySelector('[data-ls-mailto]');
    const note = box.querySelector('[data-ls-send-note]');
    if (share) share.hidden = !yes;
    if (mail) mail.hidden = yes;
    if (note) note.textContent = yes
      ? 'Choose your email app: your design file is attached. Send it to orders@legstyle.com.'
      : `Your email app opens with the message written. Attach ${this.sent.name} (in your Downloads) before you send it.`;
  }
  hideSend() { const box = this.querySelector('[data-ls-send]'); if (box) box.hidden = true; this.sent = null; }
  emailText() {
    // no community opt-in any more (Andre, 10 Oct: fewer things to read and decide)
    const s = this.sent;
    return [
      'Hi LegStyle,', '',
      `Here is my design for my cover (${s.ref}).`, '',
      `My design's name: ${this.designName() || s.designName || ''}`,
      "My clinician's name: ", '',
      ...(this.shareArt()
        ? [`Yes, I am happy for others to wear this design, or a remake of it, as the Make it your own page sets out (terms of ${ART_TERMS}). ${this.creditName() ? `Public artist credit: ${this.creditName()}` : 'No public credit, please.'}`,
           "Under 16? Your parent or guardian's name: ", '']
        : []),
      `(Attach the design file before you send: ${s.name})`,
    ].join('\n');
  }
  async shareFile() {
    const s = this.sent; if (!s) return;
    const file = new File([s.blob], s.name, { type: 'image/png' });
    // a share sheet cannot fill in the address, so the text names it (legstyle-reviewer, 10 Oct)
    try { await navigator.share({ files: [file], title: `My LegStyle design ${s.ref}`, text: 'To: orders@legstyle.com\n\n' + this.emailText().replace(/\n\(Attach[^\n]*$/, '') }); }
    catch (e) { this.useShare(false); }   // closed (perhaps no email app in the menu) or failed: the email link instead
  }
  mailtoHref() {
    const s = this.sent;
    const dn = this.designName() || s.designName;
    return `mailto:orders@legstyle.com?subject=${encodeURIComponent(`My LegStyle design ${dn ? `"${dn}" ` : ''}${s.ref}`)}&body=${encodeURIComponent(this.emailText())}`;
  }

  async copyText(txt, el, out) {
    try { await navigator.clipboard.writeText(txt); out.textContent = 'Copied'; }
    catch {
      if (!el) { out.textContent = txt; return; }          // nothing on screen to select: show it to copy by hand
      const r = document.createRange(); r.selectNodeContents(el); const s = getSelection(); s.removeAllRanges(); s.addRange(r);
      out.textContent = 'Selected: copy it with your keyboard or a long press';
    }
  }

  /* ---------- the design file: exactly what the preview shows, laid out AS THE WEARER SAW IT (v3, Andre, 10 Oct 2026:
     the old file, laid out for the template, looked shifted to anyone who opened it): the middle of the picture is the
     front and the join is at the edges (the back). 2048 px greyscale PNG, white raised. LegStyle's intake
     (make_reference.py) turns it a quarter for the template; files saved before this (v2) are already in the
     template's layout. The settings code rides inside the PNG as a text chunk, so we can always recreate it. ---------- */
  reliefPNG() {
    const S = 2048, c = document.createElement('canvas'); c.width = c.height = S;
    const x = c.getContext('2d'), im = x.createImageData(S, S);
    for (let yy = 0; yy < S; yy++) {
      const v = (yy + 0.5) / S;
      for (let xx = 0; xx < S; xx++) {
        const u = (xx + 0.5) / S;
        const g = Math.round(Math.min(1, Math.max(0, this.sampleH(u, v))) * 255);   // as on screen (v3)
        const o = (yy * S + xx) * 4; im.data[o] = im.data[o + 1] = im.data[o + 2] = g; im.data[o + 3] = 255;
      }
    }
    x.putImageData(im, 0, 0);
    return new Promise((res) => c.toBlob((b) => { c.width = c.height = 0; res(b); }, 'image/png'));
  }
  async download() {
    const msg = this.el.dlmsg, btn = this.el.dl;
    if (!this.state.img) { msg.textContent = 'Choose a picture first.'; return; }
    if (this.exporting) return;
    this.exporting = true; btn.disabled = true;
    try {
      // one consistent revision: finish any pending change now, then take the pixels and the settings together
      // (inside the try, so a failure here still unlocks the button: Astra, 6 Oct 2026)
      if (this.timer) { clearTimeout(this.timer); this.timer = null; this.processPicture(); }
      else if (this.placeTimer) { clearTimeout(this.placeTimer); this.placeTimer = null; this.placeSticker(); }
      const settings = this.settingsCode(), rev = this.rev;
      // in the file name and the settings inside it: always four characters, no 0/O or 1/I to misread
      const AB = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789', rnd = new Uint8Array(4);
      (window.crypto || {}).getRandomValues ? window.crypto.getRandomValues(rnd) : rnd.forEach((_, i) => { rnd[i] = Math.random() * 256; });
      const ref = 'MY-' + [...rnd].map((b) => AB[b % AB.length]).join('');
      // the wearer's design name in the file name too, letters and numbers only (it travels to the clinician);
      // macrons and accents drop to the plain letter first, so Kōwhai becomes Kowhai, not Ko-whai
      const designName = this.designName();
      const slug = designName.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);
      const name = `LegStyle-design-${slug ? slug + '-' : ''}${ref}.png`;
      msg.textContent = 'Making your design file...';
      if (!this.heights) throw new Error('nothing yet');
      const png = await this.reliefPNG();
      if (!png) throw new Error('encode');
      const blob = await withSettings(png, `reference ${ref} · ${settings}`);
      if (!this._live) return;
      // changed while the file was being made: this file is not what the preview shows, so it is not given out
      // (Astra, 10 Oct 2026)
      if (rev !== this.rev) {
        msg.textContent = 'You made a change while we made your file, so we did not save it. '
          + (this.el.looked && !this.el.looked.checked ? 'When you are happy with your design, tick "I have checked" again and download it.' : 'Download it again.');
        return;
      }
      // A plain download (there is no in-page save service on the real site).
      if (this.lastUrl) URL.revokeObjectURL(this.lastUrl);
      this.lastUrl = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = this.lastUrl; a.download = name;
      document.body.append(a); a.click(); a.remove();
      // the MY- reference is the design code (Andre, 10 Oct 2026; it replaces the 7 Oct "not on the page"): shown
      // with the two ways to send, so a wearer without their clinician's email can send it to us
      msg.textContent = `Check your Downloads for ${name}. Nothing there? Tap the button again.`;
      this.showSend(ref, name, blob, designName);
    } catch {
      msg.textContent = "We could not make the file on this device. Email your clinician your picture, your design's name and the settings below instead.";
      if (this.el.fallback) this.el.fallback.hidden = false;
    } finally {
      this.exporting = false;
      this.updateTicks();
    }
  }
}

if (!customElements.get('legstyle-make-it-yours')) customElements.define('legstyle-make-it-yours', LegStyleMakeItYours);
